import { useState } from "react";
import { Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      // Error handled via UI state
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0B1A3B] pt-16 md:pt-20">
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A3B] via-[#0F2251] to-[#0B1A3B]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[100px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Reach Out</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4">
            Get in Touch
          </h1>
          <p className="text-lg text-white/50 max-w-2xl mx-auto">
            Have a question, concern, or just want to share your thoughts? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-24 relative">
        <div className="absolute inset-0 bg-[#0F2251]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="md:col-span-1">
              <h2 className="text-lg font-bold text-white mb-6">Contact Info</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37]">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-white/30 mb-1">Email</p>
                    <a href="mailto:Info@CooperForNV.com" className="text-[#D4AF37] hover:text-[#F5D76E] transition-colors">
                      Info@CooperForNV.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-white/[0.03] border border-white/10">
                <h3 className="text-base font-bold text-white mb-3">Want to Help?</h3>
                <p className="text-white/40 text-sm mb-4">The best way to get involved is to volunteer! Join our team of dedicated Nevadans.</p>
                <a href="/volunteer" data-testid="contact-volunteer-link" className="inline-block text-sm font-bold text-[#D4AF37] hover:text-[#F5D76E] transition-colors">
                  Sign Up to Volunteer →
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="md:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white/[0.03] border border-white/10 p-8">
                <h2 className="text-lg font-bold text-white mb-6">Send a Message</h2>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-white/40">Your Name *</Label>
                      <Input id="name" name="name" required value={formData.name} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-white/40">Email *</Label>
                      <Input id="email" name="email" type="email" required value={formData.email} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" data-testid="contact-email-input" />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject" className="text-xs font-bold uppercase tracking-wider text-white/40">Subject *</Label>
                    <Input id="subject" name="subject" required value={formData.subject} onChange={handleInputChange} className="mt-2 rounded-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" placeholder="What's this about?" data-testid="contact-subject-input" />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-white/40">Message *</Label>
                    <Textarea id="message" name="message" required rows={6} value={formData.message} onChange={handleInputChange} className="mt-2 rounded-none resize-none bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-[#D4AF37]" placeholder="Tell us what's on your mind..." data-testid="contact-message-input" />
                  </div>

                  <button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="w-full py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(212,175,55,0.3)] disabled:opacity-50 flex items-center justify-center gap-2">
                    {isSubmitting ? "Sending..." : (<>Send Message <Send className="w-4 h-4" /></>)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
